"use client";
import { useMemo } from "react";
import { Camera, Phone, Share2 } from "lucide-react";
import { Button } from "../ui/button";
import { getProductColors, getProductValue } from "@/lib/utils/product";
import { getColor } from "@/static/colors";
import ColorSwatch from "../ui/ColorSwatch";
import { showSuccess } from "@/lib/toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";

const ActionRow = ({ label, buttonText, icon: Icon, variant, href, onClick }) => (
  <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 justify-center md:justify-between py-2">
    <div className="text-center lg:text-start lg:flex-1">{label}</div>
    <Button
      type="button"
      size="xl"
      variant={variant}
      className="max-w-[375px] w-full text-white shrink-0"
      onClick={onClick}
      asChild={!!href}>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2">
          {buttonText} <Icon />
        </a>
      ) : (
        <>
          {buttonText} <Icon />
        </>
      )}
    </Button>
  </div>
);

export default function SingleProductInfo({
  product = {},
  selectedColor,
  onColorChange,
  arEnabled = false,
  onArClick,
}) {
  const title = getProductValue(product.title) || "محصول";
  const title_dsc = getProductValue(product.title_dsc) || "";
  const description = getProductValue(product.description) || "";
  const sku = getProductValue(product.sku) || "";
  const material = getProductValue(product.material) || "";

  const uniqueColors = useMemo(() => getProductColors(product), [product]);
  const selectedColorInfo = useMemo(
    () => getColor(selectedColor),
    [selectedColor]
  );

  const handleColorClick = (code) => {
    if (onColorChange) {
      onColorChange(code);
    }
  };

  const handleShare = () => {
    const shareData = {
      title: title,
      text: `${title}${title_dsc ? ` | ${title_dsc}` : ""}`,
      url: window.location.href,
    };

    if (navigator.share) {
      navigator.share(shareData).catch((err) => {
        if (err.name !== "AbortError") {
          console.error(err);
        }
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      showSuccess("لینک محصول با موفقیت کپی شد");
    }
  };

  const actions = [
    ...(arEnabled ? [{ Icon: Camera, onClick: onArClick, label: "مشاهده در واقعیت افزوده" }] : []),
    { Icon: Share2, onClick: handleShare, label: "اشتراک‌گذاری" },
  ];

  return (
    <section className="p-8 glass-box w-full text-base lg:text-lg text-mute flex flex-col space-y-2  lg:space-y-6">
      <div className="flex justify-between items-start lg:gap-4">
        <div className="w-full">
          <div className="text-white flex justify-between items-start w-full ">
            <div className=" flex justify-between w-full">
              <div className="">
              <h1 className="text-xl lg:text-2xl lg:font-semibold leading-tight">
                {title}
              </h1>

              <p className="!text-lg font-semibold text-mute lg:!text-xl leading-tight !my-2 lg:!mt-4 ">
                {title_dsc}
              </p>
              </div>
            <div className="flex gap-2 lg:gap-3 text-white justify-end  ">
              {actions.map(({ Icon, onClick, label }, idx) => (
                <Button
                  key={idx}
                  type="button"
                  variant="berryBorder"
                  className="h-10 w-10 lg:h-11 lg:w-11 rounded-full p-0 shrink-0"
                  onClick={onClick}
                  aria-label={label}>
                  <Icon className="h-4 w-4 md:h-6 md:w-6" />
                </Button>
              ))}
            </div>
            </div>
          </div>
          {/*<div className="mt-2 lg:mt-3 text-sm lg:text-lg">{description}</div>*/}
        </div>
      </div>

      {sku && (
        <div>
          <span className="font-semibold">کد محصول :</span>{" "}
          <span className="text-white lg:font-semibold text-sm lg:text-lg ">
            {sku}
          </span>
        </div>
      )}

      {(material || uniqueColors.length > 0) && (
        <div className="flex justify-between flex-wrap md:flex-nowrap space-y-3">
          <div>
            <span className="">انتخاب طرح:</span>{" "}
            <span className="text-white lg:font-semibold text-sm lg:text-lg ">
              {selectedColorInfo?.title || material || "استاندارد"}
            </span>
          </div>

          {uniqueColors.length > 0 && (
            <div className="flex gap-x-1 w-full md:w-fit">
              <TooltipProvider>
                {uniqueColors.map((code) => (
                  <Tooltip key={code}>
                    <TooltipTrigger asChild>
                      <ColorSwatch
                        code={code}
                        onClick={() => handleColorClick(code)}
                        isSelected={selectedColor === code}
                        className="cursor-pointer"
                      />
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="font-dana text-xs">
                      {getColor(code)?.title || code}
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TooltipProvider>
            </div>
          )}
        </div>
      )}

      <div className="!mt-auto pt-4 lg:pt-6">
        {arEnabled && (
          <ActionRow
            label="پیش از خرید به خانه ببرید..."
            buttonText="مشاهده در محیط منزل"
            icon={Camera}
            variant="berryBorder"
            onClick={onArClick}
          />
        )}

        <ActionRow
          label="برای مشاوره خرید، با ما تماس بگیرید!"
          buttonText="تماس با فروشگاه"
          icon={Phone}
          variant="berry"
          href="tel:09192387002"
        />
      </div>
    </section>
  );
}
